import React from 'react';
import {
  useUserUpdateIdentityMutation,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { Form, FormOnSubmit } from 'components/form';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  SelfOnboardingStepKey,
  SelfOnboardingStepProps,
  SelfOnboardingWorkerNameFormData,
} from 'containers/selfOnboarding/steps/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import {
  StepActions,
  StepInfo,
  StepInfoTitle,
  StepJobsiteInfo,
} from 'containers/selfOnboarding/steps/components';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import { useGetIsSelfOnboardingClosed } from 'containers/selfOnboarding/helpers/useGetIsSelfOnboardingClosed';
import {
  getDefaultValues,
  getFormInputs,
  getUpdateInputs,
} from './WorkerNameStep.forms';

/**
 * Collects the worker's name (first / middle / last / suffix) — the first slice
 * of the former Basic Info step in the authenticated self-onboarding flow.
 *
 * Dual-mode, mirroring Basic Info's create/update branching:
 * - Brand-new worker (no `workerId`): the account doesn't exist yet, so the name
 *   is only staged into `state.basicInfo` and consumed later by
 *   `selfOnboardingWorkerCreate` at the Basic Info step. No server call here.
 * - Existing worker (review-initiated edit, `workerId` present): persists the
 *   name via `userUpdateIdentity` (first/last) + `updateWorker` (middle/suffix).
 *   Name fields are always editable here — this is the path that lets
 *   multi-jobsite workers finally rename themselves.
 */
export function WorkerNameStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    updateState,
    navigation,
    stepConfig,
    localize,
    language,
    jobsiteInvitation,
  } = props;
  const { basicInfo, worker, jobsiteWorker, userAccountId, contractorId } =
    state;
  const { workerId } = worker ?? {};
  const { goToNextStep, goToStep, state: navigationState } = navigation;
  const { isReview, autoFocusField } = navigationState;

  const [isSaving, setIsSaving] = React.useState(false);
  const [updateUserIdentity] = useUserUpdateIdentityMutation();
  const [updateWorker] = useUpdateWorkerMutation();
  const { isSelfOnboardingClosed } = useGetIsSelfOnboardingClosed();

  const onSubmit: FormOnSubmit<SelfOnboardingWorkerNameFormData> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      if (workerId) {
        if (await isSelfOnboardingClosed()) {
          setIsSaving(false);
          goToStep(SelfOnboardingStepKey.Closed);
          return;
        }

        const { userIdentityInput, workerInput } = getUpdateInputs(
          data,
          dirtyFields,
        );

        if (userIdentityInput) {
          await updateUserIdentity({
            variables: { input: { userAccountId, userIdentityInput } },
          });
        }

        if (workerInput) {
          await updateWorker({ variables: { workerId, workerInput } });
        }
      }

      // Stage the name for both flows: the create path reads it from
      // `state.basicInfo` at Basic Info, and the review screen reads it back.
      updateState({ basicInfo: { ...basicInfo, ...data } });

      setIsSaving(false);
      goToNextStep();
    } catch (error) {
      event.preventDefault();
      setIsSaving(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    }
  };

  const inputs = React.useMemo(() => getFormInputs(), []);
  const defaultValues = React.useMemo(
    () => getDefaultValues(basicInfo),
    [basicInfo],
  );
  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={`${localize(Copy.self_onboarding_worker_match_header)} 👋`}
          subtitle={localize(Copy.self_onboarding_worker_match_instructions)}
          withBottomBorder
          hideOnMobile
        />
        <StepJobsiteInfo
          jobsiteInvitation={jobsiteInvitation}
          jobsiteWorker={jobsiteWorker}
          contractorName={contractorId?.label}
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        autoFocus={
          (autoFocusField as keyof SelfOnboardingWorkerNameFormData) ?? true
        }
        renderBelow={
          <StepActions
            jobsiteInvitation={jobsiteInvitation}
            localize={localize}
            isReview={isReview}
            actions={isReview ? 'all' : 'continue'}
            onSkip={goToNextStep}
            continueActionWithSpinner={isSaving}
          />
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
